if (!customElements.get("page-header-logo-banner")) {
  customElements.define(
    "page-header-logo-banner",
    class PageHeaderLogoBannerSection extends HTMLElement {
      constructor() {
        super();
        this.section = this.closest("section");
        this.logoBanner = this;
        this.video = null;
        this.lowBatteryClass = "low-battery-mode";
        this.isLowBattery = false;
        this.elementToScale = this.querySelector(".wt-logo-banner__logo");
        this.header = document.querySelector(".wt-header");

        this.isSticky = false;
        this.observer = null;
        this.scrollListener = null;
        this.readyClass = "wt-logo-banner--ready";
        this.handleShopifyEditorEvent =
          this.handleShopifyEditorEvent.bind(this);

        this.frameRequested = false;
        this.isScaling = false;
        this.isTrackingHeader = false;
        this.isTimeline = false;
        this.lastWrapperHeight = null;
        this.onScroll = this.onScroll.bind(this);
        this.onResize = this.onResize.bind(this);
        this.update = this.update.bind(this);
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        document.removeEventListener(
          "shopify:section:load",
          this.handleShopifyEditorEvent,
        );

        // Disconnect observer if it exists
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }

        // Remove scroll event listener if it exists
        if (this.scrollListener) {
          document.removeEventListener("scroll", this.scrollListener);
          this.scrollListener = null;
        }

        // Remove resize event listener if it exists
        if (this.resizeListener) {
          window.removeEventListener("resize", this.resizeListener);
          this.resizeListener = null;
        }

        this.disableTimeline();
        this.layoutObserver?.disconnect();
        this.layoutObserver = null;
      }

      isMobile() {
        return window.matchMedia("(max-width: 899px)").matches;
      }

      handleShopifyEditorEvent() {
        this.reinit();
      }

      isValidSectionsOrder() {
        const pageHeader = document.body.querySelector("header.page-header");
        const currentSection = this.section;

        if (pageHeader && currentSection) {
          let sibling = pageHeader.nextElementSibling;
          while (sibling && sibling.tagName.toLowerCase() !== "section") {
            sibling = sibling.nextElementSibling;
          }
          return sibling === currentSection;
        }
        return false;
      }

      observeHeader() {
        this.isTrackingHeader = true;
        this.listen();
        this.update();
      }

      getNominalWidth() {
        const computedStyle = window.getComputedStyle(this.header);
        const nominalWidthMobile = computedStyle
          .getPropertyValue("--logo-width-mobile")
          .trim();
        const nominalWidth = computedStyle
          .getPropertyValue("--logo-width-desk")
          .trim();

        return parseInt(this.isMobile() ? nominalWidthMobile : nominalWidth);
      }

      calculateMaxScale() {
        const nominalWidth = this.getNominalWidth();
        const screenWidth = window.innerWidth;
        const maxLogoWidth = screenWidth * 0.6;
        const maxScale = maxLogoWidth / nominalWidth;
        return Math.min(maxScale, 6);
      }

      setInitialScale() {
        // this.elementToScale.style.transform = `scale(${this.calculateMaxScale()})`;
      }

      measure() {
        this.logoWrapper = this.logoBanner.querySelector(".wt-logo-banner__picture");
        this.maxScale = this.calculateMaxScale();
        // iOS fires resize while the toolbar collapses; a live innerHeight made the scale jump mid-scroll.
        const isToolbarResize =
          this.viewportHeight !== undefined &&
          window.innerWidth === this.viewportWidth &&
          window.matchMedia("(pointer: coarse)").matches;
        if (!isToolbarResize) this.viewportHeight = window.innerHeight;
        this.viewportWidth = window.innerWidth;
        this.logoWrapper.style.width = this.getBoundingClientRect().width + "px";
        if (this.isTimeline) this.measureTimeline();
      }

      // Scroll-driven CSS animation runs on the compositor (Safari 26.4+); JS scroll handlers get throttled during fast flicks.
      measureTimeline() {
        this.logoWrapper.style.height = "";
        this.elementToScale.style.transform = "";
        const bannerBottom = this.getBoundingClientRect().bottom + window.scrollY;
        const scaleStart = Math.max(0, bannerBottom - this.viewportHeight);
        const scaleEnd = Math.max(scaleStart, bannerBottom - this.header.offsetHeight);
        const wrapperHeight = this.logoWrapper.offsetHeight;
        const vars = {
          "--wt-lb-scale-max": this.maxScale,
          "--wt-lb-scale-start": `${scaleStart}px`,
          "--wt-lb-scale-end": `${scaleEnd}px`,
          "--wt-lb-shift-end": `${bannerBottom}px`,
          "--wt-lb-shift-from": `${(bannerBottom - wrapperHeight) / 2}px`,
          "--wt-lb-shift-to": `${-wrapperHeight / 2}px`,
        };
        Object.entries(vars).forEach(([name, value]) => this.style.setProperty(name, value));
      }

      handleLogoSize() {
        this.measure();
        this.applyLogoSize(this.getBoundingClientRect().bottom, this.header.offsetHeight);
      }

      applyLogoSize(containerBottom, headerHeight) {
        const logoWrapperHeight = Math.max(0, containerBottom);
        const maxDistance = this.viewportHeight - headerHeight;
        const ratio = Math.max(0, Math.min(1, (containerBottom - headerHeight) / maxDistance));
        const scale = Math.min(this.maxScale, 1 + (this.maxScale - 1) * ratio);

        this.logoBanner.classList.toggle(
          "wt-logo-banner--vanishing",
          containerBottom / this.viewportHeight < 0.7,
        );
        this.logoWrapper.classList.toggle("inactive", logoWrapperHeight < 1);
        if (this.isTimeline) return;

        if (logoWrapperHeight !== this.lastWrapperHeight) {
          this.logoWrapper.style.height = `${logoWrapperHeight}px`;
          this.lastWrapperHeight = logoWrapperHeight;
        }
        this.elementToScale.style.transform = `scale(${scale})`;
      }

      listen() {
        if (this.scrollListener) return;
        this.scrollListener = this.onScroll;
        document.addEventListener("scroll", this.scrollListener, { passive: true });
        this.resizeListener = this.onResize;
        window.addEventListener("resize", this.resizeListener);
      }

      onScroll() {
        if (this.frameRequested) return;
        this.frameRequested = true;
        window.requestAnimationFrame(this.update);
      }

      onResize() {
        if (this.isScaling) this.measure();
        this.onScroll();
      }

      enableTimeline() {
        if (this.isTimeline || !window.CSS?.supports("animation-timeline: scroll()")) return;
        this.isTimeline = true;
        this.classList.add("wt-logo-banner--timeline");
        this.measure();
      }

      observeLayout() {
        if (this.layoutObserver) return;
        this.layoutObserver = new ResizeObserver(() => {
          this.measure();
          this.onScroll();
        });
        this.layoutObserver.observe(document.documentElement);
        this.layoutObserver.observe(this.header);
      }

      disableTimeline() {
        if (!this.isTimeline) return;
        this.isTimeline = false;
        this.lastWrapperHeight = null;
        this.classList.remove("wt-logo-banner--timeline");
      }

      update() {
        this.frameRequested = false;
        const containerBottom = this.getBoundingClientRect().bottom;
        const headerHeight = this.header.offsetHeight;
        const headerBottom = this.isTrackingHeader ? this.header.getBoundingClientRect().bottom : 0;

        if (this.isScaling) this.applyLogoSize(containerBottom, headerHeight);
        if (this.isTrackingHeader) {
          this.header.classList.toggle("wt-header--transparent", containerBottom >= headerBottom);
        }
      }

      observeAndScaleElement() {
        this.setInitialScale();
        this.isScaling = true;
        this.enableTimeline();
        this.observeLayout();
        this.listen();
      }

      // calculateOffset() {
      //   const header = document.querySelector("header");
      //   const headerHeight = header.offsetHeight;
      //
      //   const announcement = document.querySelector(".wt-announcement");
      //   const announcementHeight = announcement ? announcement.offsetHeight : 0;
      //   const offset = this.isTransparentHeaderEnabled()
      //     ? headerHeight + announcementHeight
      //     : 0;
      //
      //   return offset;
      // }

      // setTopMargin() {
      //   const offset = this.calculateOffset();
      //   this.section.style.marginTop = `-${offset}px`;
      // }

      isTransparentHeaderEnabled() {
        const header = document.querySelector(".wt-header");
        return (
          header.dataset.transparent &&
          this.isValidSectionsOrder() &&
          header.classList.contains("wt-header--v3")
        );
      }

      setReadyClass() {
        this.classList.add(this.readyClass);
      }

      // With both a desktop and a mobile video, the first in the DOM is the mobile one, display: none on desktop.
      getVisibleVideo() {
        const videos = [...this.querySelectorAll(".wt-video__movie video")];
        return videos.find((video) => video.getClientRects().length > 0) || null;
      }

      init() {
        this.video = this.getVisibleVideo();
        document.addEventListener(
          "shopify:section:load",
          this.handleShopifyEditorEvent,
        );

        this.setInitialScale();
        this.handleLogoSize();

        this.setupHeaderAndObservers();
        this.initializeVideoAndScaling();

        if (!this.video) {
          this.setReadyClass();
        }
      }

      setupHeaderMode() {
        const header = document.querySelector(".wt-header");
        const stickyHeaderThreshold = document.querySelector(
          ".sticky-header__threshold",
        );
        const isHeaderSticky =
          document.body.classList.contains("page-header-sticky");

        if (this.isTransparentHeaderEnabled()) {
          const isHeaderTransparent = header.classList.contains(
            "wt-header--transparent",
          );

          if (isHeaderSticky) {
            stickyHeaderThreshold.style.height = "120vh";
            // this.setTopMargin();
          } else if (isHeaderTransparent) {
            // this.setTopMargin();
          }
        } else if (isHeaderSticky) {
          stickyHeaderThreshold.style.height = "200px";
          this.section.style.marginTop = "0";
        }
      }

      setupHeaderAndObservers() {
        this.setupHeaderMode();

        if (this.isTransparentHeaderEnabled()) {
          this.observeHeader();
        }
      }

      initializeVideoAndScaling() {
        const isDesignMode = this.dataset.designMode;
        if (!this.video || isDesignMode) {
          this.observeAndScaleElement();
        }
        if (!this.video) return;

        let isFinished = false;
        const finish = () => {
          if (isFinished) return;
          isFinished = true;

          if (this.isLowBattery) {
            this.classList.add(this.lowBatteryClass);
            this.disableTimeline();
          } else if (!isDesignMode) {
            this.observeAndScaleElement();
          }

          this.handleLogoSize();
          this.setReadyClass();
        };

        this.video
          .play()
          .catch(() => {
            this.isLowBattery = true;
          })
          .finally(finish);
        // play() can stay pending (e.g. a video that never loads), so never wait longer than 3 s.
        setTimeout(finish, 3000);
      }

      reinit() {
        this.setupHeaderAndObservers();
        setTimeout(this.handleLogoSize.bind(this), 0);
      }
    },
  );
}
